import React, { useEffect, useState } from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import { useNavigate } from "react-router-dom";
import api from "../../api";

const WAITING_LIST_PATH = "/admin/course-waiting-list";

const STAT_STYLE = {
  flex: 1,
  borderRadius: 2,
  px: 1.5,
  py: 1.2,
  textAlign: "center",
};

/**
 * Waiting list snapshot with a direct route to the campaign screen, so a new
 * course can be shared with everyone waiting for one.
 */
export default function WaitingListCard() {
  const nav = useNavigate();
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // per_page=1 keeps this cheap: only the totals are needed.
        const [all, fresh] = await Promise.all([
          api.get("admin/course-waiting-lists", { params: { per_page: 1 } }),
          api.get("admin/course-waiting-lists", { params: { per_page: 1, status: "new" } }),
        ]);
        if (cancelled) return;
        setCounts({
          total: all.data?.total || 0,
          fresh: fresh.data?.total || 0,
        });
      } catch (err) {
        if (!cancelled) setError(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#111827" }}>
        Course Waiting List
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Share a new course with everyone waiting, by email or WhatsApp.
      </Typography>

      {error ? (
        <Typography variant="body2" color="text.secondary">
          Waiting list totals are unavailable.
        </Typography>
      ) : !counts ? (
        <Stack direction="row" spacing={1.5}>
          <Skeleton variant="rounded" height={62} sx={{ flex: 1 }} />
          <Skeleton variant="rounded" height={62} sx={{ flex: 1 }} />
        </Stack>
      ) : (
        <Stack direction="row" spacing={1.5}>
          <Box sx={{ ...STAT_STYLE, bgcolor: "#ede9fe" }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: "#7c3aed" }}>
              {counts.total}
            </Typography>
            <Typography variant="caption" sx={{ color: "#7c3aed", fontWeight: 600 }}>
              On the list
            </Typography>
          </Box>
          <Box sx={{ ...STAT_STYLE, bgcolor: "#fff7ed" }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: "#ea580c" }}>
              {counts.fresh}
            </Typography>
            <Typography variant="caption" sx={{ color: "#ea580c", fontWeight: 600 }}>
              Not contacted
            </Typography>
          </Box>
        </Stack>
      )}

      <Button
        fullWidth
        startIcon={<CampaignIcon />}
        onClick={() => nav(WAITING_LIST_PATH)}
        sx={{
          mt: 2,
          background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
          color: "#fff",
          fontWeight: 600,
          borderRadius: 2,
          textTransform: "none",
          "&:hover": {
            background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
            opacity: 0.9,
          },
        }}
      >
        Share a Course
      </Button>
    </Box>
  );
}
